import type { CommandKind } from './commands';
import type { WriteOffHistory, WriteOffKind, WriteOffWhy } from './writeOffs';
import type { PermissionKey } from './permissions';
import type { GiveBackAssessment } from './cashierAllowance';

/**
 * Approval grants (ADR-023). An approval is asked for one exact operation, the approver is verified
 * separately (their PIN is checked by its own committed call, so failed attempts count toward the
 * lockout), and a short-lived single-use grant is issued. The protected command then carries
 * `approval_grant_id`; it never carries a PIN. The server binds the grant to the action, its subject
 * and terms (amount, method, lines, reason), the requesting employee and terminal, the approver, an
 * expiry and one consumption.
 */

/** Command kinds that can consume an approval grant. Mirrors the cases of app.approval_terms. */
export const APPROVAL_ACTIONS = [
  'order.send',
  'order.void_line',
  'order.void',
  'payment.void',
  'order.refund',
  'ticket.unserve',
  'order.unserve',
  /* ADR-024 */
  'shift.takeover_start',
  'shift.close_finish',
  /* ADR-031 */
  'order.write_off',
  /* ADR-032 */
  'order.return_line',
] as const satisfies readonly CommandKind[];
export type ApprovalAction = (typeof APPROVAL_ACTIONS)[number];

export function isApprovalAction(kind: string): kind is ApprovalAction {
  return (APPROVAL_ACTIONS as readonly string[]).includes(kind);
}

/**
 * Approvals of money or owner-level actions need the approver's 6-digit PIN (owner decision,
 * Phase 7 PR-0). Mirrors app.financial_approval_permissions().
 */
export const FINANCIAL_APPROVAL_PERMISSIONS = [
  'refund.approve',
  'order.void.approve',
  'shift.takeover.approve',
  'cash.receive',
  'writeoff.approve',
] as const satisfies readonly PermissionKey[];

/**
 * True when any of `permissions` is a money or owner-level approval: an approval asking for them
 * takes a 6-digit PIN, and an employee who holds one must set a 6-digit PIN.
 */
export function approvalNeedsSixDigitPin(permissions: readonly string[]): boolean {
  return permissions.some((p) => (FINANCIAL_APPROVAL_PERMISSIONS as readonly string[]).includes(p));
}

/** What the approver is shown: built by the server from the request it recorded. */
export interface ApprovalSummary {
  action: ApprovalAction;
  order_number?: string | null;
  currency?: string | null;
  requested_by?: string | null;
  reason?: string | null;
  total_minor?: number;
  unpaid_minor?: number;
  reverses_minor?: number;
  amount_minor?: number;
  captured_minor?: number;
  payment_method?: string | null;
  refund_method?: string | null;
  line?: string;
  qty?: number;
  line_total_minor?: number;
  lines?: { name: string; qty: number }[];
  ticket_seq?: number;
  /* shift approvals (ADR-024) */
  shift_cashier?: string;
  drawer?: string;
  opened_at?: string;
  idle_since?: string;
  shift_status?: string;
  counted_minor?: number;
  retained_minor?: number;
  removed_minor?: number;
  /* write-offs (ADR-031) */
  kind?: WriteOffKind;
  /** A staff meal: who ate. */
  employee?: string | null;
  balance_minor?: number;
  /** Why the owner is asked: shown, not bound (the day's usage may move). */
  why?: WriteOffWhy[];
  history?: WriteOffHistory[];
  /* returns (ADR-032): `line`, `qty` and `amount_minor` above; what goes back as money */
  refund_minor?: number;
  /** ADR-035: a refund or payment void beyond the cashier's allowance: why, and the usage read. Shown, not bound. */
  give_back?: GiveBackAssessment;
}

export interface ApprovalApprover {
  id: string;
  display_name: string;
}

/** public.request_approval: the recorded request, who may approve it and the PIN rule. */
export interface ApprovalRequestView {
  ok: true;
  request_id: string;
  action: ApprovalAction;
  permissions: string[];
  subject_type: string;
  subject_id: string;
  order_id: string | null;
  amount_minor: number | null;
  summary: ApprovalSummary;
  expires_at: string;
  six_digit_pin: boolean;
  approvers: ApprovalApprover[];
  /** ADR-033: whether the owner's phone can approve it (an owner with an authenticator), and whether it was sent. */
  remote?: { available: boolean; asked: boolean };
}

export type RequestApprovalResult =
  ApprovalRequestView | { ok: false; code: 'not_needed' | 'session_required' | 'terminal_revoked' };

/** Codes public.approve_request returns instead of raising, so a failed PIN attempt commits. */
export const APPROVAL_ERROR_CODES = [
  'pin_invalid',
  'pin_locked',
  'pin_not_set',
  'pin_six_digits_required',
  'employee_suspended',
  'approver_not_permitted',
  'self_approval',
  'request_not_found',
  'request_expired',
  /* ADR-033: the owner declined it on the phone, or this tablet withdrew it */
  'request_declined',
  'request_withdrawn',
  'session_required',
  'terminal_revoked',
] as const;
export type ApprovalErrorCode = (typeof APPROVAL_ERROR_CODES)[number];

export type ApproveRequestResult =
  | {
      ok: true;
      grant_id: string;
      approver_employee_id: string;
      expires_at: string;
      replayed?: boolean;
    }
  | {
      ok: false;
      code: ApprovalErrorCode;
      attempts_left?: number;
      retry_after_seconds?: number;
    };

/** How a consuming command explains a refused grant (`hint.approval` with code `forbidden`). */
export type GrantRefusal = 'not_found' | 'mismatch' | 'used' | 'expired' | 'changed' | 'approver';
